import {HttpClient, HttpContext, HttpResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ServerEndpoints} from '../../../server.endpoints';
import {FileUploadResult, ReviewType, Submission} from '../shared/models/submission.model';
import {SubmitSubmissionRequest} from '../shared/models/submit-request.model';
import {DEDUPE_KEY} from '../../../x-interceptors/duplicate-request.interceptor';

/**
 * Talks to the Forms service submission API (`/api/forms/v2025.1/submission`) through the gateway. Mirrors
 * {@link FormHttpService}: single BASE_URL origin, the AuthInterceptor attaches the context token. Reveal +
 * file download are role-gated server-side (SensitiveDataAccessor); a 403 surfaces if the caller lacks it.
 */
@Injectable({providedIn: 'root'})
export class SubmissionHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  private get base(): string {
    return this.baseUrl + ServerEndpoints.Forms_Submission__v2025_1;
  }

  /**
   * Submit (create) a form submission: validates server-side, encrypts sensitive fields, stores.
   *
   * Pass {@code files} (`fieldKey → File`) to send attachments **in the same request** — the body then becomes
   * `multipart/form-data` and the whole submit is atomic: it either lands with its files or not at all (#258).
   * The dedicated {@link uploadFile} endpoint cannot serve a first fill because it needs a submission id, which
   * does not exist yet.
   *
   * A multipart request carries an explicit {@link DEDUPE_KEY}, because `FormData` is a body the
   * {@link DuplicateRequestInterceptor} cannot compare and would otherwise let through un-deduped — see
   * {@link submissionDedupeKey}.
   */
  submit(request: SubmitSubmissionRequest, files?: Record<string, File>): Observable<Submission> {
    const body = multipartBody(request, files);
    return body
      ? this.http.post<Submission>(this.base, body, {context: dedupeContext('create', request, files!)})
      : this.http.post<Submission>(this.base, request);
  }

  /**
   * Update an existing **draft** submission (resume-and-continue). With {@code finalize:false} it saves the draft;
   * with {@code finalize:true} it validates and finalises. Server rejects non-draft submissions (409).
   *
   * Takes the same optional {@code files} as {@link submit}: the first-fill screen adopts an existing draft when
   * the employee saved one earlier, and that path has to carry attachments too — and needs the same
   * double-submit protection, so it carries a {@link DEDUPE_KEY} too.
   */
  update(id: string, request: SubmitSubmissionRequest, files?: Record<string, File>): Observable<Submission> {
    const body = multipartBody(request, files);
    return body
      ? this.http.put<Submission>(`${this.base}/${id}`, body, {context: dedupeContext(`update:${id}`, request, files!)})
      : this.http.put<Submission>(`${this.base}/${id}`, request);
  }

  /**
   * Start a revision of a finalised submission: clones it into a new **draft** at the current form version
   * (sensitive values re-encrypted server-side). Edit + finalise the returned draft via {@link update} to
   * resubmit a new version (the original is then superseded).
   */
  revise(id: string): Observable<Submission> {
    return this.http.post<Submission>(`${this.base}/${id}/revise`, {});
  }

  /** The caller's own submissions (metadata only — values are empty here). */
  list(): Observable<Submission[]> {
    return this.http.get<Submission[]>(this.base);
  }

  /** One submission with sensitive values masked. */
  get(id: string): Observable<Submission> {
    return this.http.get<Submission>(`${this.base}/${id}`);
  }

  /** One submission with sensitive values decrypted — requires SensitiveDataAccessor (audited server-side). */
  reveal(id: string): Observable<Submission> {
    return this.http.get<Submission>(`${this.base}/${id}/reveal`);
  }

  /**
   * Mark a submission **handled** for a review type (e.g. a payroll accountant has seen the latest answers).
   * Management/data roles only (server-gated); audited. Returns the updated submission. Idempotent.
   */
  markReviewHandled(id: string, type: ReviewType): Observable<Submission> {
    return this.http.post<Submission>(`${this.base}/${id}/review/${type}/handle`, {});
  }

  /** Clear a submission's handled marker for a review type (re-open it). Same gating as {@link markReviewHandled}. */
  markReviewUnhandled(id: string, type: ReviewType): Observable<Submission> {
    return this.http.post<Submission>(`${this.base}/${id}/review/${type}/unhandle`, {});
  }

  /** Download a decrypted attachment (requires SensitiveDataAccessor; audited). */
  downloadFile(id: string, blobId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.base}/${id}/file/${blobId}`, {observe: 'response', responseType: 'blob'});
  }

  /**
   * Download a submission as a branded PDF (management / payroll / data roles only; audited server-side). With
   * {@code unmasked:true} the sensitive fields are decrypted — that variant is gated like {@link reveal}
   * (SensitiveDataAccessor / platform owner) and a 403 surfaces otherwise. Returns the full response so the
   * caller can read the server-provided file name from `Content-Disposition`.
   */
  downloadDocument(id: string, unmasked = false, employeeName?: string): Observable<HttpResponse<Blob>> {
    const params: Record<string, string | boolean> = {unmasked};
    if (employeeName) params['employeeName'] = employeeName; // display-only; the doc shows it as the first meta row
    return this.http.get(`${this.base}/${id}/document`, {params, observe: 'response', responseType: 'blob'});
  }

  /**
   * Upload (or replace) an attachment on a **draft** submission for a given file field. The bytes are
   * client-side encrypted server-side and recorded against the draft; owner-only (write scope). Returns the
   * stored blob metadata. Submitting employees can replace but never view attachments (download is role-gated).
   */
  uploadFile(id: string, fieldKey: string, file: File): Observable<FileUploadResult> {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http.post<FileUploadResult>(`${this.base}/${id}/file/${fieldKey}`, body);
  }
}

/** Prefix every attachment part carries — mirrors the backend `AttachmentIntake.PartPrefix`. */
export const SUBMISSION_FILE_PART_PREFIX = 'file:';

/** Name of the part carrying the JSON request — mirrors the backend's `payload` part. */
export const SUBMISSION_PAYLOAD_PART = 'payload';

/**
 * Builds the `multipart/form-data` body for a submit-with-attachments, or `null` when there is nothing to attach
 * (the caller then posts plain JSON, which keeps every file-less form on the simpler, unchanged path).
 *
 * Shape: a `payload` part with the JSON request, plus one `file:<fieldKey>` part per attachment. Field keys are
 * namespaced so a form may legitimately contain a field called `payload`.
 */
function multipartBody(request: SubmitSubmissionRequest, files?: Record<string, File>): FormData | null {
  const entries = Object.entries(files ?? {});
  if (entries.length === 0) return null;

  const body = new FormData();
  body.append(SUBMISSION_PAYLOAD_PART, JSON.stringify(request));
  for (const [fieldKey, file] of entries) {
    body.append(SUBMISSION_FILE_PART_PREFIX + fieldKey, file, file.name);
  }
  return body;
}

/** The `HttpContext` a multipart submit rides with — carrying its stable dedupe identity. */
function dedupeContext(scope: string, request: SubmitSubmissionRequest, files: Record<string, File>): HttpContext {
  return new HttpContext().set(DEDUPE_KEY, submissionDedupeKey(scope, request, files));
}

/**
 * The double-submit identity of a multipart submission (#258).
 *
 * **Why it is needed at all.** `DuplicateRequestInterceptor` (Bug #143) collapses two concurrent identical
 * mutating requests onto one backend call — but only for bodies it can serialize and compare. `FormData` is
 * explicitly *not* one of those, so a submit carrying attachments would sail past the guard: a double-tap on
 * "Absenden" would create **two** `FormSubmission` rows and **two** sets of encrypted blobs on a
 * retention-dated personnel record. The file-less JSON path never lost that protection; only the multipart path
 * did, so it has to declare its identity explicitly.
 *
 * **What the key is.** Exactly the identity the JSON path already gets from `serializeBody()` — the serialized
 * request — plus the one part of the payload the interceptor cannot see: the attached files, described by
 * `fieldKey:name:size:lastModified` and sorted by field key so the map's iteration order can never change the
 * result. So the multipart path is deduped on precisely the same grounds as the JSON path, no more and no less.
 *
 * **Why it is stable across the two clicks** — the property that actually matters, and the easy thing to get
 * wrong. Every input is a *pure function of the attempt*: `buildSubmitRequest` is deterministic over the form's
 * current values, and the `File` objects come from the picker and are held unchanged by `SelectedFilesState`.
 * Nothing here is minted at send time — no `Date.now()`, no nonce — so building the key twice for the same
 * click yields the same string, which is what makes the second click collapse onto the first.
 *
 * **Why not an id minted when the form is opened.** That also works, but it introduces mutable lifecycle state
 * that must be created, threaded and re-minted at every transition (next form in the wizard, retry after a 422,
 * adopting a saved draft) — and every one of those transitions is a chance to break either the stability or the
 * distinctness. A pure derivation has no lifecycle to get wrong and is directly testable: build it twice and
 * compare.
 *
 * Two genuinely different attempts — different answers, or a different file picked — produce different keys and
 * both go out, which is the correct behaviour: dedupe must never merge submissions that are not the same.
 */
export function submissionDedupeKey(
  scope: string, request: SubmitSubmissionRequest, files: Record<string, File>): string {
  const parts = Object.entries(files)
    .map(([fieldKey, file]) => `${fieldKey}:${file.name}:${file.size}:${file.lastModified}`)
    .sort();

  return `submission:${scope}|${JSON.stringify(request)}|${parts.join(',')}`;
}
